import { createAction } from '@reduxjs/toolkit';
import { prefetchManager } from './apiSSRModule/prefetchManager';
import type { AnyApi } from './buildCreateApi';
import type { EndpointDefinitions, CombinedState, QueryActionCreatorResult } from './rtk-query-types';
import type { GetStoreFn } from './types';

export const globalInitialStateVariable = '__REDUX_INITIAL_STATE__';

declare global {
    // eslint-disable-next-line no-var, @typescript-eslint/no-explicit-any -- DIRECT-239681
    var __REDUX_INITIAL_STATE__: Record<string, any> | undefined;
}

/**
 * Метод, подготавливающий данные редьюсера, полученные при SSR, для гидрации на клиенте.
 *
 * @param moduleName – Название модуля.
 * @param reducerPath – Путь по которому редьюсер подключается в стор.
 * @returns Подготовленные данные редьюсера для гидрации стора на клиенте.
 * @internal
 */
export function extractReducerRehydrationInfo(moduleName: string, reducerPath: string) {
    return global[globalInitialStateVariable]?.[moduleName]?.[reducerPath];
}

/**
 * Для запросов, в которых есть гидрированные данные, создаем подписки в сторе.
 * Это необходимо для того, чтобы данные в сторе не были удалены быстрее, чем гидрируется React компонент.
 * Такой случай возможен при выключении кеширования в эндпоинте.
 *
 * @param getStore – Получить инстанс стора для текущего запроса
 * @param api – Инстанс RTK Query API
 * @param state – Данные для гидрации
 * @internal
 */
export function subscribeToHydratedApiData<
    Definitions extends EndpointDefinitions,
    ReducerPath extends string,
    TagTypes extends string,
>(
    getStore: GetStoreFn,
    api: AnyApi,
    state: CombinedState<NoInfer<Definitions>, NoInfer<TagTypes>, NoInfer<ReducerPath>>
) {
    for (const [cacheKey, queryStatus] of Object.entries(state.queries)) {
        const endpointName = queryStatus?.endpointName;
        if (!endpointName) {
            continue;
        }

        const endpoint = api.endpoints[endpointName];
        if (!endpoint) {
            continue;
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- DIRECT-239681
        const action = endpoint.initiate(queryStatus.originalArgs!);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- DIRECT-239681
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        const promise = getStore().dispatch(action) as QueryActionCreatorResult<Definitions[string]>;
        const promiseKey = `${api.reducerPath}/${cacheKey}`;

        prefetchManager.set(promiseKey, { promise });
    }
}

/**
 * Создает экшен для гидрации данных в RTK Query API.
 *
 * @param moduleName – Название модуля.
 * @param reducerPath – reducerPath, переданный в `createApi()`.
 * @returns Экшен для гидрации данных в RTK Query API.
 * @internal
 */
export function createHydrateApiAction<ApiState>(moduleName: string, reducerPath: string) {
    return createAction<ApiState>(`${moduleName}/${reducerPath}/hydrate`);
}

/**
 * Создает экшен для гидрации данных в слайсе.
 *
 * @param sliceName – name, переданный в `createSlice()`.
 * @returns Экшен для гидрации данных в слайсе.
 * @internal
 */
export function createHydrateSliceAction(sliceName: string) {
    return createAction(`${sliceName}/hydrate`);
}
